// @ts-strict-ignore
import moment, {Moment} from 'moment-timezone';
import {ParamComponent} from 'panel';

import {annotationsClient} from 'common/report/queries/app-api/annotations-client';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {track, TrackingEvent, TrackingEventProperty} from 'common/charts/visualization/line-chart/annotations/tracking';
import {getReportName} from 'common/report/util';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {MenuChangeEvent} from 'common/components/select/types';
import {currentProjectStore} from 'common/report/stores/project-details';
import {Permission} from 'common/report/constants';
import {useStore} from 'common/util/component-helpers';

import {AnnotationEvent} from '../type';
import {template} from './template';
import css from './index.inline.styl';

const timeItems = [
  `12:00am`,
  `1:00am`,
  `2:00am`,
  `3:00am`,
  `4:00am`,
  `5:00am`,
  `6:00am`,
  `7:00am`,
  `8:00am`,
  `9:00am`,
  `10:00am`,
  `11:00am`,
  `12:00pm`,
  `1:00pm`,
  `2:00pm`,
  `3:00pm`,
  `4:00pm`,
  `5:00pm`,
  `6:00pm`,
  `7:00pm`,
  `8:00pm`,
  `9:00pm`,
  `10:00pm`,
  `11:00pm`,
].map((hour) => ({label: hour}));

type CreateAnnotationFormParams = {
  /**
   * date of annotation with format "MMM D, YYYY HH:mm:ss"`,
   * */
  date: string;
  /**
   * utc offset from project, which is only necessary if you want incomplete interval zoning
   * */
  utcOffset: number;
};

type CreateAnnotationFormJsxProps = {
  params: CreateAnnotationFormParams;
  on: {
    annotationCreated: (ev: CustomEvent<AnnotationEvent>) => void;
    closeAnnotationPopup: () => void;
  };
};

export interface CreateAnnotationFormState {
  /** the date of the annotation */
  date: Moment;
  /** open and closes the calendar component */
  dateInputFocused: boolean;
  /** description of the annotation */
  description: string;
  /** gives user error message when trying to save an annotation and description is blank */
  invalidDescription: boolean;
  /** used to determine if the save annotation button should be disabled or not */
  saving: boolean;
  /** temporary date when user selects different dates on the calendar but has not selected the select date button */
  tmpDate: Moment;
}

export class CreateAnnotationForm extends ParamComponent<CreateAnnotationFormParams, CreateAnnotationFormState> {
  get config() {
    return {
      css,
      useShadowDom: true,
      params: {
        date: String,
        utcOffset: Number,
      },
      template,
      helpers: {
        getTimeItems: () => timeItems,

        getTimeInput: () => {
          return this.querySelector(`.annotation-time-input`);
        },

        descriptionTextareaInserted: (vnode) =>
          requestAnimationFrame(() => {
            vnode.elm.focus();
            vnode.elm.select();
          }),

        handleTimeSelectChange: (ev) => {
          const newDate = moment(ev.detail.item.label, `h:mma`);
          const date = this.state.date.hour(newDate.hour()).minute(newDate.minute());
          this.update({date});
        },

        handleDateSelected: (ev) => {
          this.update({tmpDate: moment(ev.detail.date)});
        },

        handleSelectDateButtonClicked: () => {
          const newDate = moment(this.state.tmpDate);
          const date = this.state.date.year(newDate.year()).month(newDate.month()).date(newDate.date());
          this.update({date, dateInputFocused: false});
        },

        handleDateSelectChange: ({detail}: MenuChangeEvent) => {
          this.update({dateInputFocused: detail.isOpen});
        },

        updateDescription: (ev) => {
          this.update({
            description: ev.target.value,
            invalidDescription: false,
          });
        },

        cancelCreation: () => {
          this.dispatchEvent(new CustomEvent(`closeAnnotationPopup`));
        },

        hasValidUserPermissions: () => {
          return (
            currentProjectStore.hasFetchedValue() &&
            currentProjectStore.value.hasPermission(Permission.WriteAnnotations)
          );
        },

        saveAnnotation: async () => {
          if (!this.state.description) {
            this.update({invalidDescription: true});
            return;
          }

          const annotationProjectTime = this.toAbsoluteProjectTime(this.state.date);

          this.update({saving: true});
          if (this.helpers.hasValidUserPermissions()) {
            const annotation = await annotationsClient.createAnnotation({
              projectId: parseInt(getProjectId(), 10),
              annotation: {
                date: annotationProjectTime.format(`YYYY-MM-DD HH:mm:ss`),
                description: this.state.description,
              },
              workspaceId: parseInt(getWorkspaceId(), 10),
            });

            this.dispatchEvent(new CustomEvent<AnnotationEvent>(`annotationCreated`, {detail: {annotation}}));

            track(TrackingEvent.Add, {
              [TrackingEventProperty.AnnotationText]: this.state.description,
              [TrackingEventProperty.AnnotationTimestamp]: this.state.date.format(`YYYY-MM-DD HH:mm:ss`),
              [TrackingEventProperty.ReportName]: getReportName(),
            });
          }
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    useStore(this, currentProjectStore).fetch();
  }

  get date(): moment.Moment {
    // Initialize state from params
    if (!this.state.date) {
      const paramDate = moment(this.params.date, `MMM D, YYYY HH:mm:ss`);
      this.update({date: paramDate});
      return paramDate;
    }
    return this.state.date;
  }

  getFormattedTime(format, date = this.date) {
    return date.format(format);
  }

  /** using utc-offset, convert date to project time */
  toAbsoluteProjectTime(date) {
    const projectTzOffset = this.params.utcOffset;
    return projectTzOffset ? moment(date).clone().utcOffset(projectTzOffset, true) : date;
  }
}

export const CreateAnnotationFormEl = defineAndMakeJsxFactory<CreateAnnotationFormJsxProps>(
  `create-annotation-form`,
  CreateAnnotationForm,
);
export const CreateAnnotationFormReact = panel2react<CreateAnnotationFormJsxProps>(
  `create-annotation-form`,
  CreateAnnotationForm,
);
